<template>
  <!-- 
      是非題PvP遊戲多點觸控優化樣式：
      - touch-action: none        → 禁用瀏覽器預設觸控手勢（縮放、滾動），確保多點觸控正常
      - user-select: none         → 防止觸控時選取文字，避免干擾遊戲
      - -webkit-touch-callout     → 禁用 iOS 長按選單（複製、貼上等）
      - -webkit-user-select       → iOS Safari 舊版兼容性，防止文字選取
  -->
  <div class="pvptfgame-container" style="touch-action: none; user-select: none; -webkit-touch-callout: none; -webkit-user-select: none;">


    <!-- 游戏区域 -->
    <div class="game-area" ref="gameAreaRef">
     

      <div class="game-header">
        <div class="game-topic">{{ gameConfig.topic }}</div>
      </div>
      <div class="timer">{{ formatTime(timeRemaining) }}
        <button class="start-button" @click="toggleGame">
          <svg-icon :icon-class="gameStarted ? (gamePaused ? 'play' : 'pause') : 'play'" />
        </button>
      </div>

      <!-- 遊戲開始前彈窗區 -->
      <div v-if="beforeGame" class="before-game-container">
        <div class="before-game-modal">
          <div class="game-title">{{ gameConfig.topic }}</div>
          <div class="game-info">
            <div class="game-time">{{ $t('PvPTFgame.gameDuration') }}： {{ gameConfig.gameDuration }} {{ $t('PvPTFgame.second') }}</div>
           
          </div>
          <div class="game-buttons">
            <button class="action-button play-button" @click="showReadyGoCountdown()">
              <svg-icon icon-class="play" class="play-icon"/>
            </button>
            <button v-if="!hasConfigUrl" class="action-button setting-button" @click="goToSettings()">
              <svg-icon icon-class="setting-game" class="setting-icon"/>
            </button>
            <!-- WebView 環境才顯示推送按鈕 -->
            <button v-if="isWebView" class="action-button push-button" 
                    :class="{ 'disabled': isSendDisabled }"
                    @click="handleSendClick()">
              <svg-icon icon-class="upload" class="sendweb-icon" />
            </button>
          </div>
        </div>
      </div>

      
      <!-- Ready, Go! 提示 -->
      <div v-if="showReadyGo" class="ready-go-container">
        <div class="ready-go-text">{{ readyGoText }}</div>
      </div>

      <!-- 遊戲暫停彈窗 -->
      <div v-if="gameStarted && gamePaused && !gameOver" class="pause-game-container">
        <div class="pause-game-modal">
          <div class="pause-title">{{ $t('PvPTFgame.gamePaused') || '活動暫停' }}</div>
          <div class="pause-buttons">
            <button class="action-button continue-button" @click="resumeGame()">
              <svg-icon icon-class="play"  class="play-icon"/>
            </button>
            <button class="action-button restart-button" @click="resetGame()">
              <svg-icon icon-class="refresh" class="refresh-icon" />
            </button>
          </div>
        </div>
      </div>

      <!-- 左侧游戏区域 -->
      <div class="game-side left-side">
        <div class="left-score">{{ leftScore }}</div>
        <div class="game-content-wrap">
         
          
          <!-- 左側問題區域 -->
          <div class="question-wrap">
            <!-- 星空背景層 -->
            <div class="stars-background">
              <div v-for="(star, index) in leftStars" :key="'left-'+index" 
                   class="star" 
                   :style="{ 
                     left: star.x + '%', 
                     top: star.y + '%', 
                     opacity: star.opacity,
                     width: star.size + 'px',
                     height: star.size + 'px',
                     backgroundColor: star.color,
                     animationDuration: getStarAnimationDuration(star, leftRocketPower) + 's'
                   }">
              </div>
            </div>
            
            <div class="question-card" :class="{ 'answered': leftAnswered }">
              <div class="question-number">Q{{ leftQuestionIndex + 1 }}</div>
              <div class="question-text">{{ leftQuestion && leftQuestion.text ? leftQuestion.text : '' }}</div>
              <div class="question-buttons">
                <button class="answer-btn circle-btn" 
                        @click="answerQuestion(true, 'left')" 
                        @touchstart="handleTouchStart($event, true, 'left')"
                        @touchend="handleTouchEnd($event)"
                        :disabled="(leftAnswered && gameConfig.answerMode === 'sync') || leftQuestionCompleted">
                  <span><svg-icon icon-class="circle" /></span>
                </button>
                <button class="answer-btn cross-btn" 
                        @click="answerQuestion(false, 'left')" 
                        @touchstart="handleTouchStart($event, false, 'left')"
                        @touchend="handleTouchEnd($event)"
                        :disabled="(leftAnswered && gameConfig.answerMode === 'sync') || leftQuestionCompleted">
                  <span><svg-icon icon-class="close" /></span>
                </button>
              </div>
              <div class="answer-status" v-if="leftAnswered && gameConfig.answerMode === 'sync'">
                <span>{{ $t('PvPTFgame.answered') }}</span>
              </div>
            </div>
          </div>
           <!-- 左側火箭效果區域 -->
           <div class="rocket-effect-area left-rocket">
            <HTrocket :power="leftRocketPower" :showPowerLevel="false" :questionCount="gameConfig.questionList ? gameConfig.questionList.length : 1" />
          </div>
        </div>
        <!-- ... -->
      </div>

      <!-- 右侧游戏区域 -->
      <div class="game-side right-side">
        <div class="right-score">{{ rightScore }}</div>
        <div class="game-content-wrap">
           <!-- 右側火箭效果區域 -->
           <div class="rocket-effect-area right-rocket">
            <HTrocket :power="rightRocketPower" :showPowerLevel="false"  :rocketImage="require('@/assets/HTwbActAssets/PvPTFgameObj/red_rocket.webp')" :questionCount="gameConfig.questionList ? gameConfig.questionList.length : 1"/>
          </div>
          <!-- 右側問題區域 -->
          <div class="question-wrap">
            <!-- 星空背景層 -->
            <div class="stars-background">
              <div v-for="(star, index) in rightStars" :key="'right-'+index" 
                   class="star" 
                   :style="{ 
                     left: star.x + '%', 
                     top: star.y + '%', 
                     opacity: star.opacity,
                     width: star.size + 'px',
                     height: star.size + 'px',
                     backgroundColor: star.color,
                     animationDuration: getStarAnimationDuration(star, rightRocketPower) + 's'
                   }">
              </div>
            </div>
            
            <div class="question-card" :class="{ 'answered': rightAnswered }">
              <div class="question-number">Q{{ currentQuestionIndex + 1 }}</div>
              <div class="question-text">{{ currentQuestion && currentQuestion.text ? currentQuestion.text : '' }}</div>
              <div class="question-buttons">
                <button class="answer-btn circle-btn" 
                        @click="answerQuestion(true, 'right')" 
                        @touchstart="handleTouchStart($event, true, 'right')"
                        @touchend="handleTouchEnd($event)"
                        :disabled="(rightAnswered && gameConfig.answerMode === 'sync') || rightQuestionCompleted">
                  <span><svg-icon icon-class="circle" /></span>
                </button>
                <button class="answer-btn cross-btn" 
                        @click="answerQuestion(false, 'right')" 
                        @touchstart="handleTouchStart($event, false, 'right')"
                        @touchend="handleTouchEnd($event)"
                        :disabled="(rightAnswered && gameConfig.answerMode === 'sync') || rightQuestionCompleted">
                  <span><svg-icon icon-class="close" /></span>
                </button>
              </div>
              <div class="answer-status" v-if="rightAnswered && gameConfig.answerMode === 'sync'">
                <span>{{ $t('PvPTFgame.answered') }}</span>
              </div>
            </div>
          </div>
          
         
        </div>
      </div>

      <!-- 飛行分數動畫 -->
      <transition-group name="flying-score" tag="div">
        <div
          v-for="effect in flyingScoreEffects"
          :key="effect.id"
          class="flying-score-effect"
          :style="{
            left: effect.currentX + 'px',
            top: effect.currentY + 'px',
            color: effect.isCorrect ? 'blue' : 'red',
            opacity: effect.opacity
          }"
        >
          {{ effect.text }}
        </div>
      </transition-group>

    <!-- 遊戲結束彈窗 -->
    <div v-if="gameOver" class="game-over-modal">
      <div class="game-result-container" v-show="!showAnswerModal">
        <!-- 左側結果卡片 -->
        <div class="result-card-wrap">
          <div class="win-act" v-show="isLeftWinner">
            <img src="@/assets/HTwbActKitCSS/shine.png" alt="">
          </div>
        <div class="result-card left-result" >
          <div class="result-header">
            <div class="result-title" :class="{ 'win-title': isLeftWinner, 'tie-title': isTie, 'lose-title': !isLeftWinner && !isTie }">
              {{ isLeftWinner ? $t('PvPTFgame.win') : (isTie ? $t('PvPTFgame.tie') : '') }}
            </div>
          </div>
          <div class="result-score">
            <div class="star-score">
              <span class="score-number">{{ leftScore }}</span>
            </div>
          </div>
          <div class="result-details">
            <div class="detail-item">
              {{ $t('PvPTFgame.correctItems') }}: {{ leftCorrectCount }} {{ $t('PvPTFgame.items') }}
            </div>
            <div class="detail-item">
              {{ $t('PvPTFgame.errorItems') }}: {{ leftWrongCount }} {{ $t('PvPTFgame.items') }}
            </div>
            <div class="detail-item" v-if="gameConfig.answerMode === 'individual'">
              {{ $t('PvPTFgame.answerTime') }}: {{ formatAnswerTime(leftAnswerTime) }}
            </div>
            <!-- <div class="detail-item">
              {{ $t('PvPTFgame.maxCombo') }}: {{ leftMaxCombo }} {{ $t('PvPTFgame.items') }}
            </div> -->
          </div>
        </div>
        </div>

        <!-- 右側結果卡片 -->
         <div class="result-card-wrap" v-show="!showAnswerModal">
          <div class="win-act" v-show="isRightWinner">
            <img src="@/assets/HTwbActKitCSS/shine.png" alt="">
          </div>
        <div class="result-card right-result" >
         
          <div class="result-header">
            <div class="result-title" :class="{ 'win-title': isRightWinner, 'tie-title': isTie, 'lose-title': !isRightWinner && !isTie }">
              {{ isRightWinner ? $t('PvPTFgame.win') : (isTie ? $t('PvPTFgame.tie') : '') }}
            </div>
          </div>
          <div class="result-score">
            <div class="star-score">
              <span class="score-number">{{ rightScore }}</span>
            </div>
          </div>
          <div class="result-details">
            <div class="detail-item">
              {{ $t('PvPTFgame.correctItems') }}: {{ rightCorrectCount }} {{ $t('PvPTFgame.items') }}
            </div>
            <div class="detail-item">
              {{ $t('PvPTFgame.errorItems') }}: {{ rightWrongCount }} {{ $t('PvPTFgame.items') }}
            </div>
            <div class="detail-item" v-if="gameConfig.answerMode === 'individual'">
              {{ $t('PvPTFgame.answerTime') }}: {{ formatAnswerTime(rightAnswerTime) }}
            </div>
            <!-- <div class="detail-item">
              {{ $t('PvPTFgame.maxCombo') }}: {{ rightMaxCombo }} {{ $t('PvPTFgame.items') }}
            </div> -->
          </div>
          </div>
        </div>
      </div>
      <!-- 答案彈窗 -->
      <div v-if="showAnswerModal" class="answer-modal-mask">
          <div class="answer-modal-card">
            <div class="answer-modal-header">
              <span class="answer-modal-title">{{ $t('PvPTFgame.referenceAnswers') }}</span>
              <button class="answer-modal-close" @click="toggleAnswerModal">×</button>
            </div>
            <div class="answer-modal-content-container">
              <div class="answer-modal-content">
                <div class="answer-modal-section">
                  <div class="answer-label-container">
                    <div class="answer-label correct">O</div>
                  </div>
                  <div class="answer-list">
                    <span v-for="(question, idx) in gameConfig.questionList.filter(q => q.isTrue)" :key="'c-'+idx" class="answer-item correct">{{ question.text }}</span>
                  </div>
                </div>
                <div class="answer-modal-section">
                  <div class="answer-label-container">
                    <div class="answer-label wrong">X</div>
                  </div>
                  <div class="answer-list">
                    <span v-for="(question, idx) in gameConfig.questionList.filter(q => !q.isTrue)" :key="'e-'+idx" class="answer-item wrong">{{ question.text }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

      <!-- 底部按鈕區 -->
      <div class="game-result-actions" v-show="!showAnswerModal">
        <button class="action-button answer-button" @click="toggleAnswerModal">
          <span class="answer-text">{{ $t('PvPTFgame.showAnswers') }}</span>
        </button>
        <button class="action-button restart-button" @click="resetGame">
          <svg-icon icon-class="refresh" class="refresh-icon" />
        </button>
      </div>
    </div>
  </div>
    </div>
</template>

<script setup>
/* eslint-disable */
import { ref, reactive, onMounted, onUnmounted, computed, nextTick, inject } from 'vue';
import HTrocket from './HTrocket.vue';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import SvgIcon from '@/components/SvgIcon.vue';

const router = useRouter();
const { t } = useI18n();
const iviewMessage = inject("$Message");

// 選擇器引用
const gameAreaRef = ref(null);
const leftSideRef = ref(null);
const rightSideRef = ref(null);

// 遊戲狀態
const gameStarted = ref(false);
const gamePaused = ref(false);
const gameOver = ref(false);
const timeRemaining = ref(0);
const leftScore = ref(0);
const rightScore = ref(0);

// 音效引用
const backgroundMusic = ref(null);
const correctSound = ref(null);
const wrongSound = ref(null);
const tieSound = ref(null);
const winSound = ref(null);
const readySound = ref(null);
const goSound = ref(null);

// 通信工具類
const MessageBridge = {
    // 環境類型
    ENV: {
        WEBVIEW: 'webview',
        WEB: 'web'
    },
    // 當前環境
    currentEnv: 'webview',
    // 初始化
    init() {
        this.currentEnv = window?.chrome?.webview ? this.ENV.WEBVIEW : this.ENV.WEB;
        return this;
    },
    // 發送消息
    postMessage(data, origin = '*') {
        if (this.currentEnv === this.ENV.WEBVIEW) {
            window?.chrome?.webview.postMessage(data);
        } else {
            window.postMessage(data, origin);
        }
    },
    // 添加消息監聽器
    addEventListener(callback) {
        if (this.currentEnv === this.ENV.WEBVIEW) {
            window?.chrome?.webview.addEventListener('message', event => {
                callback(event);
            });
        } else {
            window.addEventListener('message', event => {
                callback(event);
            });
        }
    }
};

// 初始化消息橋接器
const messageBridge = MessageBridge.init();

// WebView 環境檢測
const isWebView = computed(() => {
    return messageBridge.currentEnv === MessageBridge.ENV.WEBVIEW;
});

// 推送按鈕冷卻狀態
const isSendDisabled = ref(false);
const cooldownTimer = ref(null);

// 配置加載狀態
const hasConfigUrl = ref(false);
const configUrl = ref('');

let gameConfig = reactive({
  topic: 'empty state',
  correctVocabularyList: [],
  errorVocabularyList: [],
  gameDuration: 0,
  background: '',
  questionList: [],
  leftQuestionList: [], // 左側獨立問題列表
  answerMode: 'sync' // 作答模式: 'sync' 或 'individual'
});

// 從 blob URL 載入配置
const loadConfigFromUrl = async (url) => {
    try {
        const response = await fetch(url);
        
        if (!response.ok) {
            throw new Error('載入失敗');
        }
        
        const config = await response.json();
        const configData = JSON.parse(config.data.gameConfig);
        
        // 特殊處理 gameDuration，計算為 questionList.length * questionDuration
        if (configData.questionList && configData.questionDuration) {
            configData.gameDuration = configData.questionList.length * configData.questionDuration;
        }
        
        Object.assign(gameConfig, configData);
        timeRemaining.value = gameConfig.gameDuration;
        
        // 如果有問題列表，為左側創建一個打亂順序的問題列表
        if (configData.questionList && configData.questionList.length > 0) {
            gameConfig.leftQuestionList = [...configData.questionList]
                .sort(() => Math.random() - 0.5);
        }
        
        iviewMessage.success({
            content: t('PvPTFgame.configLoadSuccess'),
            duration: 2,
            closable: true
        });
    } catch (error) {
        iviewMessage.error({
            content: t('PvPTFgame.configLoadFailed'),
            duration: 3,
            closable: true
        });
        
        // 使用預設配置
        const defaultConfig = {
            "topic": "TFgame(Demo)",
            "questionList": [
              {
                "text": "地球上含量最多的氣體是氮氣，約佔大氣成分的百分之七十八。",
                "isTrue": true
              },
              {
                "text": "台灣於1895年被清朝割讓給日本，開始了長達五十年的殖民統治。",
                "isTrue": true
              },
              {
                "text": "在台灣，冬季季風主要從東北方吹來，帶來較濕冷的天氣。",
                "isTrue": true
              },
              {
                "text": "台灣的中央山脈是島上最主要的山脈，從南到北縱貫整個島嶼。",
                "isTrue": true
              },
              {
                "text": "太陽是一顆恆星，為地球提供光與熱，是生命的重要能源。",
                "isTrue": true
              }
            ],
            "questionDuration": 10,
            "answerMode": "individual"
        };
        
        defaultConfig.gameDuration = defaultConfig.questionList.length * defaultConfig.questionDuration;
        Object.assign(gameConfig, defaultConfig);
        timeRemaining.value = gameConfig.gameDuration;
        
        // 為左側創建一個打亂順序的問題列表
        gameConfig.leftQuestionList = [...defaultConfig.questionList]
            .sort(() => Math.random() - 0.5);
    }
};

onMounted(async () => {
    // 檢查是否有 config_url 參數 - 使用原本的方式
    const fullUrl = window.location.href;
    const configUrlIndex = fullUrl.indexOf('config_url=');
    
    if (configUrlIndex !== -1) {
        // 取得 config_url= 之後的所有內容（直到字串結束）
        configUrl.value = fullUrl.substring(configUrlIndex + 'config_url='.length);
        hasConfigUrl.value = true;
        await loadConfigFromUrl(configUrl.value);
    } else {
        hasConfigUrl.value = false;
        
        // 發送初始化訊息，並接收遊戲配置
        messageBridge.postMessage({ action: 'Init.PvPTFGame', data: { //到時候如果該頁沒有設置就用demo
            "topic": "TFgame(Demo)",
            "questionList": [
              {
                "text": "地球上含量最多的氣體是氮氣，約佔大氣成分的百分之七十八。",
                "isTrue": true
              },
              {
                "text": "台灣於1895年被清朝割讓給日本，開始了長達五十年的殖民統治。",
                "isTrue": true
              },
              {
                "text": "在台灣，冬季季風主要從東北方吹來，帶來較濕冷的天氣。",
                "isTrue": true
              },
              {
                "text": "台灣的中央山脈是島上最主要的山脈，從南到北縱貫整個島嶼。",
                "isTrue": true
              },
              {
                "text": "太陽是一顆恆星，為地球提供光與熱，是生命的重要能源。",
                "isTrue": true
              },
              {
                "text": "水的三態變化包括固態、液態與氣態，會隨溫度而轉換。",
                "isTrue": true
              },
              {
                "text": "植物透過光合作用吸收二氧化碳並釋放氧氣，是生態系的重要一環。",
                "isTrue": true
              },
              {
                "text": "月亮的引力會影響地球上的潮汐現象，造成漲潮與退潮。",
                "isTrue": true
              },
              {
                "text": "台灣位於環太平洋地震帶，因此地震活動相對頻繁。",
                "isTrue": true
              },
              {
                "text": "人類的體溫通常維持在約攝氏三十六至三十七度之間。",
                "isTrue": true
              }
            ],
            "questionDuration": 10,
            "answerMode": "individual"
        } });
        
        messageBridge.addEventListener(event => {
            const msg = event.data;
            console.log(msg);
            if (msg.action === 'Init.PvPTFGame') {
                console.log(msg.data, 'Init.PvPTFGame');
                
                // 特殊處理 gameDuration，計算為 questionList.length * questionDuration
                if (msg.data.questionList && msg.data.questionDuration) {
                    msg.data.gameDuration = msg.data.questionList.length * msg.data.questionDuration;
                }
                Object.assign(gameConfig, msg.data);
                
                // 將設定存入 sessionStorage
                sessionStorage.setItem('PvPTFGameConfig', JSON.stringify(msg.data));
                
                // 如果有問題列表，為左側創建一個打亂順序的問題列表
                if (msg.data.questionList && msg.data.questionList.length > 0) {
                    // 複製問題列表並打亂順序
                    gameConfig.leftQuestionList = [...msg.data.questionList]
                        .sort(() => Math.random() - 0.5);
                }
            }
        });
    }
});


const currentQuestionIndex = ref(0);
const currentQuestion = ref({ text: '', isTrue: false });

// 左側問題索引和問題
const leftQuestionIndex = ref(0);
const leftQuestion = ref({ text: '', isTrue: false });

// 答題狀態
const leftAnswered = ref(false);
const rightAnswered = ref(false);
const leftAnswer = ref(null);
const rightAnswer = ref(null);
const waitingForNextQuestion = ref(false);

// 作答時間記錄
const leftAnswerTime = ref(0); // 左側總作答時間
const rightAnswerTime = ref(0); // 右側總作答時間
const leftCurrentQuestionTime = ref(0); // 左側當前題目作答時間
const rightCurrentQuestionTime = ref(0); // 右側當前題目作答時間

// 各自進度跳題模式相關狀態
const leftQuestionCompleted = ref(false); // 左側是否完成當前題目
const rightQuestionCompleted = ref(false); // 右側是否完成當前題目

// 遊戲結果統計
const leftCorrectCount = ref(0);
const leftWrongCount = ref(0);
const leftMaxCombo = ref(0);
const rightCorrectCount = ref(0);
const rightWrongCount = ref(0);
const rightMaxCombo = ref(0);
const leftCurrentCombo = ref(0);
const rightCurrentCombo = ref(0);

// Ready, Go! 相關狀態

// 答案彈窗狀態
const showAnswerModal = ref(false);
// 是否顯示正確答案
const showCorrectAnswer = ref(true);

// 答案彈窗切換，與 gameOver 互為相反
function toggleAnswerModal() {
showAnswerModal.value = !showAnswerModal.value;
}

const showReadyGo = ref(false);
const readyGoText = ref('');
const isCountingDown = ref(false);

// 火箭火力等級 (根據答對題數調整)
const leftRocketPower = ref(0);
const rightRocketPower = ref(0);
const maxRocketPower = 10; // 最大火力等級

// 星空背景相關變數
const leftStars = ref([]);
const rightStars = ref([]);
const starCount = 30; // 每側星星數量

// 掉落物品
const leftItems = ref([]);
const rightItems = ref([]);

// 動畫分數效果
const flyingScoreEffects = ref([]);


// 游戏循环和计时器
let gameLoop = null;
let timerInterval = null;

// 游戏区域尺寸
let gameAreaWidth = 0;
let gameAreaHeight = 0;

// 遊戲開始前彈窗
const beforeGame = ref(true);

// 格式化时间显示
const formatTime = (seconds) => {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
};

// 格式化作答時間顯示
const formatAnswerTime = (seconds) => {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
};

// 勝利判定邏輯
const isLeftWinner = computed(() => {
  if (leftScore.value > rightScore.value) {
    return true;
  } else if (leftScore.value < rightScore.value) {
    return false;
  } else {
    // 分數相同時，在 individual 模式下比較作答時間
    if (gameConfig.answerMode === 'individual') {
      return leftAnswerTime.value < rightAnswerTime.value;
    }
    return false;
  }
});

const isRightWinner = computed(() => {
  if (rightScore.value > leftScore.value) {
    return true;
  } else if (rightScore.value < leftScore.value) {
    return false;
  } else {
    // 分數相同時，在 individual 模式下比較作答時間
    if (gameConfig.answerMode === 'individual') {
      return rightAnswerTime.value < leftAnswerTime.value;
    }
    return false;
  }
});

const isTie = computed(() => {
  if (leftScore.value !== rightScore.value) {
    return false;
  }
  // 分數相同時，在 sync 模式下為平局，在 individual 模式下比較作答時間
  if (gameConfig.answerMode === 'sync') {
    return true;
  } else {
    return leftAnswerTime.value === rightAnswerTime.value;
  }
});

// 切換遊戲狀態（開始/暫停/繼續）
const toggleGame = () => {
  if (!gameStarted.value) {
    if (!isCountingDown.value) {
      showReadyGoCountdown();
    }
  } else if (gamePaused.value) {
    resumeGame();
  } else {
    pauseGame();
  }
};

// 顯示 Ready, Go! 倒數效果
const showReadyGoCountdown = () => {
// 停止並重置所有音效
if (readySound.value) {
  readySound.value.pause();
  readySound.value.currentTime = 0;
}
if (goSound.value) {
  goSound.value.pause();
  goSound.value.currentTime = 0;
}
if (backgroundMusic.value) {
  backgroundMusic.value.pause();
  backgroundMusic.value.currentTime = 0;
}
if (correctSound.value) {
  correctSound.value.pause();
  correctSound.value.currentTime = 0;
}
if (wrongSound.value) {
  wrongSound.value.pause();
  wrongSound.value.currentTime = 0;
}
if (tieSound.value) {
  tieSound.value.pause();
  tieSound.value.currentTime = 0;
}
if (winSound.value) {
  winSound.value.pause();
  winSound.value.currentTime = 0;
}



  beforeGame.value = false;
  isCountingDown.value = true;
  showReadyGo.value = true;
  readyGoText.value = 'Ready';
  
  // 在 Ready 階段就將時間重置為總題目時間
  timeRemaining.value = gameConfig.questionList.length * gameConfig.questionDuration;
  
  // 播放 Ready 音效
  if (readySound.value) {
    readySound.value.currentTime = 0;
    readySound.value.play();
  }
  
  // 2秒後顯示 Go!
  setTimeout(() => {
    readyGoText.value = 'Go!';
    
    // 播放 Go 音效
    if (goSound.value) {
      goSound.value.currentTime = 0;
      goSound.value.play();
    }
    
    // 再過1秒後開始遊戲
    setTimeout(() => {
      showReadyGo.value = false;
      isCountingDown.value = false;
      startGame();
    }, 1000);
  }, 2000);
};

// 開始遊戲
const startGame = () => {
  
  // 初始化遊戲區域尺寸
  if (gameAreaRef.value) {
    const rect = gameAreaRef.value.getBoundingClientRect();
    gameAreaWidth = rect.width;
    gameAreaHeight = rect.height;
  }

  // 重置遊戲狀態
  gameStarted.value = true;
  gamePaused.value = false;
  gameOver.value = false;
  leftScore.value = 0;
  rightScore.value = 0;
  timeRemaining.value = gameConfig.questionList.length * gameConfig.questionDuration; // 設置總時間
  leftItems.value = [];
  rightItems.value = [];
  
  // 重置統計數據
  leftCorrectCount.value = 0;
  leftWrongCount.value = 0;
  leftMaxCombo.value = 0;
  rightCorrectCount.value = 0;
  rightWrongCount.value = 0;
  rightMaxCombo.value = 0;
  leftCurrentCombo.value = 0;
  rightCurrentCombo.value = 0;
  
  // 重置作答時間
  leftAnswerTime.value = 0;
  rightAnswerTime.value = 0;
  leftCurrentQuestionTime.value = gameConfig.questionDuration;
  rightCurrentQuestionTime.value = gameConfig.questionDuration;
  
  // 重置各自進度狀態
  leftQuestionCompleted.value = false;
  rightQuestionCompleted.value = false;

  // 設置第一個問題
  currentQuestionIndex.value = 0;
  leftQuestionIndex.value = 0;
  if (gameConfig.questionList && gameConfig.questionList.length > 0) {
    // 右側問題
    currentQuestion.value = gameConfig.questionList[0];
    // 左側問題
    leftQuestion.value = gameConfig.leftQuestionList[0];
    // 記錄第一題的開始時間
    currentQuestionStartTime.value = timeRemaining.value;
  }

  // 播放背景音樂
  if (backgroundMusic.value) {
    backgroundMusic.value.play();
  }

  // 啟動計時器
  if (gameConfig.answerMode === 'sync') {
    // sync 模式：統一計時
    timerInterval = setInterval(() => {
      if (timeRemaining.value > 0) {
        timeRemaining.value--;
        
        const questionElapsedTime = gameConfig.questionDuration - (timeRemaining.value % gameConfig.questionDuration);
        
        if (questionElapsedTime >= gameConfig.questionDuration && timeRemaining.value > 0) {
          checkAnswers();
        }
      } else {
        if (currentQuestionIndex.value < gameConfig.questionList.length - 1) {
          goToNextQuestion();
        } else {
          endGame();
        }
      }
    }, 1000);
  } else {
    // individual 模式：各自計時
    leftCurrentQuestionTime.value = gameConfig.questionDuration;
    rightCurrentQuestionTime.value = gameConfig.questionDuration;
    
    timerInterval = setInterval(() => {
      // 總計時器
      if (timeRemaining.value > 0) {
        timeRemaining.value--;
      }
      
      // 左側題目計時（如果還沒完成所有題目）
      if (!leftQuestionCompleted.value && leftCurrentQuestionTime.value > 0) {
        leftCurrentQuestionTime.value--;
      }
      
      // 右側題目計時（如果還沒完成所有題目）
      if (!rightQuestionCompleted.value && rightCurrentQuestionTime.value > 0) {
        rightCurrentQuestionTime.value--;
      }
      
      // 檢查是否兩邊都完成所有題目
      if (leftQuestionCompleted.value && rightQuestionCompleted.value) {
        endGame();
      }
    }, 1000);
  }
};

// 暫停遊戲
const pauseGame = () => {
  if (!gameStarted.value || gameOver.value) return;

  gamePaused.value = true;

  // 暫停背景音樂
  if (backgroundMusic.value) {
    backgroundMusic.value.pause();
  }

  // 清除定時器
  clearInterval(gameLoop);
  clearInterval(timerInterval);
};

// 繼續遊戲
const resumeGame = () => {
  if (!gameStarted.value || gameOver.value || !gamePaused.value) return;

  // 確保暫停狀態被正確設置為 false
  gamePaused.value = false;
  
  // 強制更新 DOM
  nextTick(() => {
    // 繼續播放背景音樂
    if (backgroundMusic.value) {
      backgroundMusic.value.play();
    }

    // 重新啟動計時器
    if (gameConfig.answerMode === 'sync') {
      timerInterval = setInterval(() => {
        if (timeRemaining.value > 0) {
          timeRemaining.value--;
          
          const questionElapsedTime = gameConfig.questionDuration - (timeRemaining.value % gameConfig.questionDuration);
          
          if (questionElapsedTime >= gameConfig.questionDuration && timeRemaining.value > 0) {
            checkAnswers();
          }
        } else {
          if (currentQuestionIndex.value < gameConfig.questionList.length - 1) {
            goToNextQuestion();
          } else {
            endGame();
          }
        }
      }, 1000);
    } else {
      // individual 模式：各自計時
      timerInterval = setInterval(() => {
        // 總計時器
        if (timeRemaining.value > 0) {
          timeRemaining.value--;
        }
        
        // 左側題目計時（如果還沒完成所有題目）
        if (!leftQuestionCompleted.value && leftCurrentQuestionTime.value > 0) {
          leftCurrentQuestionTime.value--;
        }
        
        // 右側題目計時（如果還沒完成所有題目）
        if (!rightQuestionCompleted.value && rightCurrentQuestionTime.value > 0) {
          rightCurrentQuestionTime.value--;
        }
        
        if (leftQuestionCompleted.value && rightQuestionCompleted.value) {
          endGame();
        }
      }, 1000);
    }
  });
};

// 结束游戏
const endGame = () => {
  gameStarted.value = false;
  gameOver.value = true;

  // 停止背景音樂
  if (backgroundMusic.value) {
    backgroundMusic.value.pause();
    backgroundMusic.value.currentTime = 0;
  }
  
  // 根據遊戲結果播放相應音效
  setTimeout(() => {
    if (leftScore.value === rightScore.value) {
      // 平局
      if (tieSound.value) {
        tieSound.value.currentTime = 0;
        tieSound.value.play();
      }
    } else {
      // 有人獲勝
      if (winSound.value) {
        winSound.value.currentTime = 0;
        winSound.value.play();
      }
    }
  }, 500); // 延遲半秒再播放，讓畫面先切換

  // 清除定时器
  clearInterval(gameLoop);
  clearInterval(timerInterval);
};

// 重置游戏
const resetGame = () => {
  // 清除所有計時器
  clearInterval(timerInterval);
  clearInterval(gameLoop);
  
  // 重置遊戲狀態
  gameStarted.value = false; // 確保遊戲狀態設置為未開始
  gamePaused.value = false;  // 確保暫停狀態設置為 false
  gameOver.value = false;
  beforeGame.value = true;
  timeRemaining.value = gameConfig.gameDuration;
  currentQuestionIndex.value = 0;
  waitingForNextQuestion.value = false;
  
  // 重置分數和統計
  leftScore.value = 0;
  rightScore.value = 0;
  leftCorrectCount.value = 0;
  rightCorrectCount.value = 0;
  leftWrongCount.value = 0;
  rightWrongCount.value = 0;
  leftCurrentCombo.value = 0;
  rightCurrentCombo.value = 0;
  leftMaxCombo.value = 0;
  rightMaxCombo.value = 0;
  
  // 重置作答時間
  leftAnswerTime.value = 0;
  rightAnswerTime.value = 0;
  leftCurrentQuestionTime.value = 0;
  rightCurrentQuestionTime.value = 0;
  
  // 重置各自進度狀態
  leftQuestionCompleted.value = false;
  rightQuestionCompleted.value = false;
  
  // 重置火箭火力
  leftRocketPower.value = 0;
  rightRocketPower.value = 0;
  
  // 重置答案状态
  resetAnswerStatus();
  
  // 清理觸控狀態
  processedTouches.value.clear();
  sideLocked.value.clear();
  
  // 重置飛行分數效果
  flyingScoreEffects.length = 0;
  
  // 初始化問題列表
  if (gameConfig.questionList && gameConfig.questionList.length > 0) {
    currentQuestion.value = gameConfig.questionList[0];
    leftQuestion.value = gameConfig.leftQuestionList[0];
  }
};

// 重置答案状态
const resetAnswerStatus = () => {
  leftAnswered.value = false;
  rightAnswered.value = false;
  leftAnswer.value = null;
  rightAnswer.value = null;
  
  // 清理觸控狀態（每題重置）
  processedTouches.value.clear();
  sideLocked.value.clear();
};

// 觸控處理標記，追蹤每一邊的觸控狀態
const processedTouches = ref(new Map());
// 側邊鎖定狀態，防止同一邊同時按圈叉
const sideLocked = ref(new Set());


// 是非題遊戲觸控開始處理 - 支援多點觸控但防止同一邊重複操作
const handleTouchStart = (event, answer, side) => {
  if (!gameStarted.value || gamePaused.value || gameOver.value) return;
  
  // 檢查該側是否已經回答（僅在 sync 模式下或已完成所有題目時禁用）
  if (gameConfig.answerMode === 'sync') {
    if ((side === 'left' && leftAnswered.value) || (side === 'right' && rightAnswered.value)) {
      event.preventDefault();
      return;
    }
  } else {
    // individual 模式下，檢查是否已完成所有題目
    if ((side === 'left' && leftQuestionCompleted.value) || (side === 'right' && rightQuestionCompleted.value)) {
      event.preventDefault();
      return;
    }
  }
  
  // 檢查該側是否已被鎖定（防止同時按圈叉）
  if (sideLocked.value.has(side)) {
    event.preventDefault();
    return;
  }
  
  // 鎖定該側
  sideLocked.value.add(side);
  
  // 遍歷所有新的觸控點
  for (let i = 0; i < event.changedTouches.length; i++) {
    const touch = event.changedTouches[i];
    const touchId = touch.identifier;
    
    // 防止重複處理同一個觸控點
    if (!processedTouches.value.has(touchId)) {
      processedTouches.value.set(touchId, { answer, side });
      
      // 立即觸發答題，不等 touchend
      answerQuestion(answer, side);
      
      // 延遲清除觸控標記和側邊鎖定
      setTimeout(() => {
        processedTouches.value.delete(touchId);
        sideLocked.value.delete(side);
      }, 200); // 200ms 防止快速重複點擊
      
      break; // 只處理第一個觸控點
    }
  }
  
  // 阻止預設行為，避免觸發 click 事件
  event.preventDefault();
  event.stopPropagation();
};

// 觸控結束處理 - 清理狀態
const handleTouchEnd = (event) => {
  // 清理結束的觸控點標記
  for (let i = 0; i < event.changedTouches.length; i++) {
    const touch = event.changedTouches[i];
    const touchData = processedTouches.value.get(touch.identifier);
    if (touchData) {
      processedTouches.value.delete(touch.identifier);
      // 短暫延遲後解除側邊鎖定
      setTimeout(() => {
        sideLocked.value.delete(touchData.side);
      }, 100);
    }
  }
};

// 回答问题
const answerQuestion = (answer, side) => {
  if (!gameStarted.value || gamePaused.value || gameOver.value) return;
  
  // 檢查是否能繼續作答
  if (gameConfig.answerMode === 'sync') {
    // sync 模式：如果已經回答過，則不再處理
    if ((side === 'left' && leftAnswered.value) || (side === 'right' && rightAnswered.value)) return;
  } else {
    // individual 模式：如果已完成所有題目，則不再處理
    if ((side === 'left' && leftQuestionCompleted.value) || (side === 'right' && rightQuestionCompleted.value)) return;
  }
  
  // 記錄答案和作答時間
  if (side === 'left') {
    leftAnswered.value = true;
    leftAnswer.value = answer;
    // 記錄左側作答時間
    if (gameConfig.answerMode === 'individual') {
      // individual 模式：計算從題目開始到現在的時間
      const questionTime = gameConfig.questionDuration - leftCurrentQuestionTime.value;
      leftAnswerTime.value += Math.max(1, questionTime); // 至少1秒
    } else {
      // sync 模式：使用原來的計算方式
      const questionTime = gameConfig.questionDuration - (timeRemaining.value % gameConfig.questionDuration);
      leftCurrentQuestionTime.value = questionTime;
      leftAnswerTime.value += questionTime;
    }
  } else {
    rightAnswered.value = true;
    rightAnswer.value = answer;
    // 記錄右側作答時間
    if (gameConfig.answerMode === 'individual') {
      // individual 模式：計算從題目開始到現在的時間
      const questionTime = gameConfig.questionDuration - rightCurrentQuestionTime.value;
      rightAnswerTime.value += Math.max(1, questionTime); // 至少1秒
    } else {
      // sync 模式：使用原來的計算方式
      const questionTime = gameConfig.questionDuration - (timeRemaining.value % gameConfig.questionDuration);
      rightCurrentQuestionTime.value = questionTime;
      rightAnswerTime.value += questionTime;
    }
  }
  
  if (gameConfig.answerMode === 'individual') {
    // individual 模式：立即評分並跳下一題
    if (side === 'left') {
      checkLeftAnswer();
      goToNextQuestionForSide('left');
    } else {
      checkRightAnswer();
      goToNextQuestionForSide('right');
    }
  } else {
    // sync 模式：等待兩邊都回答
    if (leftAnswered.value && rightAnswered.value && !waitingForNextQuestion.value) {
      waitingForNextQuestion.value = true;
      checkAnswers();
    }
  }
};

// individual 模式下檢查左側答案
const checkLeftAnswer = () => {
  const isLeftCorrect = leftAnswer.value === leftQuestion.value.isTrue;
  
  if (isLeftCorrect) {
    leftScore.value += 1;
    leftCorrectCount.value += 1;
    leftCurrentCombo.value += 1;
    if (leftCurrentCombo.value > leftMaxCombo.value) {
      leftMaxCombo.value = leftCurrentCombo.value;
    }
    updateRocketPower('left');
    if (correctSound.value) {
      correctSound.value.currentTime = 0;
      correctSound.value.play();
    }
    addFlyingScoreEffect('left', true);
  } else {
    leftWrongCount.value += 1;
    leftCurrentCombo.value = 0;
    if (wrongSound.value) {
      wrongSound.value.currentTime = 0;
      wrongSound.value.play();
    }
    addFlyingScoreEffect('left', false);
  }
};

// individual 模式下檢查右側答案
const checkRightAnswer = () => {
  const isRightCorrect = rightAnswer.value === currentQuestion.value.isTrue;
  
  if (isRightCorrect) {
    rightScore.value += 1;
    rightCorrectCount.value += 1;
    rightCurrentCombo.value += 1;
    if (rightCurrentCombo.value > rightMaxCombo.value) {
      rightMaxCombo.value = rightCurrentCombo.value;
    }
    updateRocketPower('right');
    addFlyingScoreEffect('right', true);
  } else {
    rightWrongCount.value += 1;
    rightCurrentCombo.value = 0;
    addFlyingScoreEffect('right', false);
  }
};

// individual 模式下單方跳下一題
const goToNextQuestionForSide = (side) => {
  if (side === 'left') {
    leftAnswered.value = false;
    leftAnswer.value = null;
    
    // 檢查是否還有下一題
    if (leftQuestionIndex.value + 1 < gameConfig.leftQuestionList.length) {
      leftQuestionIndex.value += 1;
      leftQuestion.value = gameConfig.leftQuestionList[leftQuestionIndex.value];
      leftCurrentQuestionTime.value = gameConfig.questionDuration; // 重置左側題目計時
    } else {
      leftQuestionCompleted.value = true;
    }
  } else {
    rightAnswered.value = false;
    rightAnswer.value = null;
    
    // 檢查是否還有下一題
    if (currentQuestionIndex.value + 1 < gameConfig.questionList.length) {
      currentQuestionIndex.value += 1;
      currentQuestion.value = gameConfig.questionList[currentQuestionIndex.value];
      rightCurrentQuestionTime.value = gameConfig.questionDuration; // 重置右側題目計時
    } else {
      rightQuestionCompleted.value = true;
    }
  }
  
  // 檢查是否兩邊都完成所有題目
  if (leftQuestionCompleted.value && rightQuestionCompleted.value) {
    endGame();
  }
};

// 檢查答案並評分
const checkAnswers = () => {
  // 左側使用左側問題檢查答案，右側使用右側問題檢查答案
  const isLeftCorrect = leftAnswer.value === leftQuestion.value.isTrue;
  const isRightCorrect = rightAnswer.value === currentQuestion.value.isTrue;
  
  // 計算需要顯示的效果數量
  let effectsToShow = 0;
  let effectsCompleted = 0;
  
  // 更新左側分數和統計
  if (isLeftCorrect) {
    leftScore.value += 1;
    leftCorrectCount.value += 1;
    leftCurrentCombo.value += 1;
    if (leftCurrentCombo.value > leftMaxCombo.value) {
      leftMaxCombo.value = leftCurrentCombo.value;
    }
    // 更新左側火箭火力
    updateRocketPower('left');
    // 播放正確音效
    if (correctSound.value) {
      correctSound.value.currentTime = 0;
      correctSound.value.play();
    }
    
    // 添加左側飛行分數效果
    effectsToShow++;
    addFlyingScoreEffect('left', true, () => {
      effectsCompleted++;
      checkAllEffectsCompleted(effectsToShow, effectsCompleted);
    });
  } else {
    leftWrongCount.value += 1;
    leftCurrentCombo.value = 0;
    // 播放錯誤音效
    if (wrongSound.value) {
      wrongSound.value.currentTime = 0;
      wrongSound.value.play();
    }
    
    // 添加左側飛行分數效果
    effectsToShow++;
    addFlyingScoreEffect('left', false, () => {
      effectsCompleted++;
      checkAllEffectsCompleted(effectsToShow, effectsCompleted);
    });
  }
  
  // 更新右側分數和統計
  if (isRightCorrect) {
    rightScore.value += 1;
    rightCorrectCount.value += 1;
    rightCurrentCombo.value += 1;
    if (rightCurrentCombo.value > rightMaxCombo.value) {
      rightMaxCombo.value = rightCurrentCombo.value;
    }
    // 更新右側火箭火力
    updateRocketPower('right');
    
    // 添加右側飛行分數效果
    effectsToShow++;
    addFlyingScoreEffect('right', true, () => {
      effectsCompleted++;
      checkAllEffectsCompleted(effectsToShow, effectsCompleted);
    });
  } else {
    rightWrongCount.value += 1;
    rightCurrentCombo.value = 0;
    
    // 添加右側飛行分數效果
    effectsToShow++;
    addFlyingScoreEffect('right', false, () => {
      effectsCompleted++;
      checkAllEffectsCompleted(effectsToShow, effectsCompleted);
    });
  }
};

// 檢查所有效果是否完成
// 記錄是否已經安排了下一題的轉換
// 防止多次呼叫 goToNextQuestion 導致計時器抖動
let nextQuestionScheduled = false;

const checkAllEffectsCompleted = (total, completed) => {
  // 如果已經安排了下一題的轉換，則不再重複安排
  if (completed >= total && !nextQuestionScheduled) {
    // 標記已經安排了下一題的轉換
    nextQuestionScheduled = true;
    
    // 所有效果都已完成，進入下一題
    // 添加額外延遲，確保所有效果都已完全消失
    setTimeout(() => {
      goToNextQuestion();
      // 重置標記，為下一次做準備
      nextQuestionScheduled = false;
    }, 500); // 延遲半秒進入下一題，讓用戶有時間看到效果
  }
};

// 初始化星星
const initStars = () => {
  leftStars.value = [];
  rightStars.value = [];
  
  for (let i = 0; i < starCount; i++) {
    // 左側星星
    leftStars.value.push(createStar());
    // 右側星星
    rightStars.value.push(createStar());
  }
};

// 創建星星對象
const createStar = () => {
  return {
    x: Math.random() * 100, // 隨機水平位置
    y: Math.random() * 100, // 隨機垂直位置
    size: Math.random() * 4 + 1, // 隨機大小 1-5px
    opacity: Math.random() * 0.7 + 0.3, // 隨機透明度 0.3-1.0
    baseDuration: Math.random() * 5 + 5, // 基礎動畫持續時間 5-10秒
    color: getRandomStarColor() // 隨機星星顏色
  };
};

// 生成隨機星星顏色
const getRandomStarColor = () => {
  // 星星顏色陣列 - 主要是白色和淺藍色系
  const colors = [
    '#ffffff', // 白色
    '#f0f8ff', // 愛麗絲藍
    '#e6e6fa', // 薔衣草色
    '#f0ffff', // 天藍色
    '#fffafa', // 雪白色
    '#f8f8ff', // 幽靈白
    '#f5f5f5'  // 白煙色
  ];
  return colors[Math.floor(Math.random() * colors.length)];
};

// 根據火力等級計算星星動畫持續時間
const getStarAnimationDuration = (star, power) => {
  // 基礎動畫時間
  const baseDuration = star.baseDuration;
  // 火力等級越高，動畫速度越快（持續時間越短）
  // 增強火力等級對速度的影響，使用更大的係數(1.5)和更小的最小值(0.1)
  const speedFactor = Math.max(0.1, 1 - (power / maxRocketPower) * 1.5);
  return baseDuration * speedFactor;
};

// 更新火箭火力
const updateRocketPower = (side) => {
  if (side === 'left') {
    leftRocketPower.value = leftCorrectCount.value;
  } else {
    rightRocketPower.value = rightCorrectCount.value;
  }
};

// 飛行分數效果的動畫時間（毫秒）
const FLYING_SCORE_ANIMATION_DURATION = 1200;

// 添加飛行分數效果
const addFlyingScoreEffect = (side, isCorrect, callback) => {
  const sideElement = side === 'left' ? document.querySelector('.left-side') : document.querySelector('.right-side');
  if (sideElement) {
    const rect = sideElement.getBoundingClientRect();
    const effectId = Date.now() + (side === 'left' ? 1 : 2);
    
    // 建立新的效果元素
    const effectElement = document.createElement('div');
    effectElement.className = 'flying-score-effect';
    effectElement.style.position = 'absolute';
    effectElement.style.left = `${rect.left + rect.width / 2}px`;
    effectElement.style.top = `${rect.top + rect.height / 2}px`;
    effectElement.style.transform = 'translate(-50%, -50%)';
    effectElement.style.fontSize = '2.5rem';
    effectElement.style.fontWeight = 'bold';
    effectElement.style.color = isCorrect ? '#4CAF50' : '#F44336';
    effectElement.style.zIndex = '9999';
    effectElement.style.pointerEvents = 'none';
    effectElement.style.transition = `all ${FLYING_SCORE_ANIMATION_DURATION}ms ease-out`;
    effectElement.textContent = isCorrect ? '+1' : '✘';
    
    // 將元素添加到文檔中
    document.body.appendChild(effectElement);
    
    // 強制重新計算樣式，確保過渡效果生效
    void effectElement.offsetWidth;
    
    // 設置動畫終點樣式
    effectElement.style.transform = `translate(-50%, -${rect.height}px) scale(1.5)`;
    effectElement.style.opacity = '0';
    
    // 動畫完成後移除元素並回調
    setTimeout(() => {
      if (document.body.contains(effectElement)) {
        document.body.removeChild(effectElement);
      }
      // 回調函數，通知效果已完成
      if (callback) callback();
    }, FLYING_SCORE_ANIMATION_DURATION);
  } else {
    // 如果元素不存在，仍然調用回調以避免卡住
    if (callback) callback();
  }
};

// 記錄每題的開始時間
const currentQuestionStartTime = ref(0);

// 前進到下一題
const goToNextQuestion = () => {
  // 確保所有飛行分數效果已清除
  const flyingEffects = document.querySelectorAll('.flying-score-effect');
  flyingEffects.forEach(el => {
    if (document.body.contains(el)) {
      document.body.removeChild(el);
    }
  });
  
  // 重置答題狀態
  leftAnswered.value = false;
  rightAnswered.value = false;
  leftAnswer.value = null;
  rightAnswer.value = null;
  waitingForNextQuestion.value = false;
  
  // 前進到下一題
  currentQuestionIndex.value += 1;
  leftQuestionIndex.value += 1;
  
  if (gameConfig.questionList && currentQuestionIndex.value < gameConfig.questionList.length) {
    // 更新右側問題
    currentQuestion.value = gameConfig.questionList[currentQuestionIndex.value];
    // 更新左側問題
    leftQuestion.value = gameConfig.leftQuestionList[leftQuestionIndex.value];
    
    // 重置計時器為剩餘題目的總時間
    const remainingQuestions = gameConfig.questionList.length - currentQuestionIndex.value;
    timeRemaining.value = remainingQuestions * gameConfig.questionDuration;
    // 記錄當前題目開始時間
    currentQuestionStartTime.value = timeRemaining.value;
  } else if (gameConfig.questionList) {
    // 如果已經是最後一題，則結束遊戲
    endGame();
  }
};  

// 前往設置頁面
const goToSettings = () => {
  router.push('/ht-pvp-tfgame-setting');
};

// 推送到終端
const sendToWebIRS = () => {
    messageBridge.postMessage({action: 'SendGame.WebIRS', data: {game: 'pvpTFGame', gameConfig: JSON.stringify(gameConfig)}});
};

// 處理推送按鈕點擊
const handleSendClick = () => {
    console.log('按鈕點擊，當前禁用狀態：', isSendDisabled.value);
    
    if (isSendDisabled.value) {
        iviewMessage.warning({
            content: t('PvPTFgame.sendCooldown'),
            duration: 3,
            closable: true
        });
        return;
    }
    
    // 執行推送
    sendToWebIRS();
    iviewMessage.success({
        content: t('PvPTFgame.sendSuccess'),
        duration: 3,
        closable: true
    });
    
    // 設置冷卻狀態
    isSendDisabled.value = true;
    console.log('設置為禁用狀態：', isSendDisabled.value);
    
    // 1分鐘後恢復
    cooldownTimer.value = setTimeout(() => {
        isSendDisabled.value = false;
        console.log('恢復為啟用狀態：', isSendDisabled.value);
    }, 60000); // 60秒
};

// 组件挂载时
onMounted(() => {
  // 初始化星空背景
  initStars();
  
  // 初始化游戏区域尺寸
  if (gameAreaRef.value) {
    const rect = gameAreaRef.value.getBoundingClientRect();
    gameAreaWidth = rect.width;
    gameAreaHeight = rect.height;
  }

  // 设置初始时间
  timeRemaining.value = gameConfig.gameDuration;

  // 初始化背景音樂
  backgroundMusic.value = new Audio(require('@/assets/HTwbActAssets/gameSounds/happy-children-352377.mp3'));
  backgroundMusic.value.loop = true;
  backgroundMusic.value.volume = 0.7; // 設置音量為50%
  
  // 初始化正確和錯誤音效
  correctSound.value = new Audio(require('@/assets/HTwbActAssets/gameSounds/shine-6-268910.mp3'));
  correctSound.value.volume = 0.6;
  
  wrongSound.value = new Audio(require('@/assets/HTwbActAssets/gameSounds/wrong-answer-129254.mp3'));
  wrongSound.value.volume = 0.6;
  
  // 初始化平局和獲勝音效
  tieSound.value = new Audio(require('@/assets/HTwbActAssets/gameSounds/game-176807.mp3'));
  tieSound.value.volume = 0.7;
  
  winSound.value = new Audio(require('@/assets/HTwbActAssets/gameSounds/orchestral-win-331233.mp3'));
  winSound.value.volume = 0.7;
  
  // 初始化 Ready 和 Go 音效
  readySound.value = new Audio(require('@/assets/HTwbActAssets/gameSounds/freeTTS-ready.mp3'));
  readySound.value.volume = 1;
  
  goSound.value = new Audio(require('@/assets/HTwbActAssets/gameSounds/freeTTS-go.mp3'));;
  goSound.value.volume = 1;
  // 設置 Go 音效的開始時間，跳過 Ready 部分
  goSound.value.currentTime = 0; // 根據音檔的實際情況調整
});

// 组件卸载时清除定时器
onUnmounted(() => {
  clearInterval(gameLoop);
  clearInterval(timerInterval);
  clearTimeout(cooldownTimer.value);
  
  // 清理觸控狀態
  processedTouches.value.clear();
  sideLocked.value.clear();
  
  // 停止並釋放背景音樂
  if (backgroundMusic.value) {
    backgroundMusic.value.pause();
    backgroundMusic.value.src = '';
    backgroundMusic.value = null;
  }
  
  // 釋放正確和錯誤音效
  if (correctSound.value) {
    correctSound.value.src = '';
    correctSound.value = null;
  }
  
  if (wrongSound.value) {
    wrongSound.value.src = '';
    wrongSound.value = null;
  }
  
  // 釋放平局和獲勝音效
  if (tieSound.value) {
    tieSound.value.src = '';
    tieSound.value = null;
  }
  
  if (winSound.value) {
    winSound.value.src = '';
    winSound.value = null;
  }
  
  // 釋放 Ready 和 Go 音效
  if (readySound.value) {
    readySound.value.src = '';
    readySound.value = null;
  }
  
  if (goSound.value) {
    goSound.value.src = '';
    goSound.value = null;
  }
});
</script>

<style lang="less" scoped>
@import '../assets/HTwbActKitCSS/HTpvpTFGame.less';

// 推送按鈕樣式
.push-button {
    width: 60px;
    height: 60px;
    background-color: #2196F3; /* 藍色 */
    border: none;
    border-radius: 50%;
    display: flex;
    justify-content: center;
    align-items: center;
    cursor: pointer;
    transition: transform 0.2s, background-color 0.2s;
    
    .sendweb-icon{
        width: 30px !important;
        height: 30px !important;
        *{
            fill: white !important;
        }
    }

    &:hover:not(.disabled) {
        transform: scale(1.1);
        background-color: #1976D2;
    }
}

.push-button.disabled {
    background-color: #9e9e9e !important; /* 灰色，使用 !important 確保優先級 */
    cursor: not-allowed !important;
    
    .sendweb-icon {
        * {
            fill: #666666 !important;
        }
    }
}

/* 遊戲內容區域使用 flex 佈局 */
.game-content-wrap {
  display: flex;
  width: 100%;
  height: 100%;
}

/* 問題區域樣式 */
.question-wrap {
  flex: 1.5;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 20px;
  position: relative;
  background-color: #0a1a2a; /* 深藍色背景與火箭區域一致 */
  overflow: hidden;
}

/* 星空背景層樣式 */
.stars-background {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 0;
}

/* 星星樣式 */
.star {
  position: absolute;
  border-radius: 50%;
  animation: starFall linear infinite;
}

@keyframes starFall {
  from {
    transform: translateY(-100%);
  }
  to {
    transform: translateY(100vh);
  }
}

/* 確保問題卡片在星空之上 */
.question-card {
  position: relative;
  z-index: 1;
}

/* 火箭效果區域樣式 */
.rocket-effect-area {
  flex: 1;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: #0a1a2a; /* 深藍色背景 */
  height: 100%;
  position: relative;
  overflow: hidden;
}


/* 左右火箭區域特別樣式 */
.left-rocket {
  border-right: 1px solid rgba(255, 255, 255, 0.1);
}

.right-rocket {
  border-left: 1px solid rgba(255, 255, 255, 0.1);
}
</style>
